import React, { FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import BrandLogo from '../components/BrandLogo';
import api from '../services/api';

const pillars = [
  {
    title: 'Real estate',
    body: 'Light commercial and residential holdings with defined geographies and exit discipline—vehicles that put construction to work and recycle capital through the cycle.',
  },
  {
    title: 'Construction',
    body: 'An operating construction capability that delivers work product for our real estate strategies and grows with the capital we put behind it.',
  },
  {
    title: 'Software & operating systems',
    body: 'Tools that simplify construction management and bidding, plus a software team that builds and maintains tooling for the network—including government-contracting experience.',
  },
  {
    title: 'Capital management',
    body: 'Money-management tooling already running and compounding capital—hardened and expanded as the ecosystem produces profits.',
  },
];

const work = [
  {
    label: 'Central Texas residential',
    line: 'Turnkey and specialty residential strategies held through the cycle, with construction capacity on commercial terms.',
  },
  {
    label: 'Light commercial / flex',
    line: 'Metal buildings and flex warehouse build–lease–sell in markets we know, with a path toward energy production and utilities.',
  },
  {
    label: 'Construction-adjacent software',
    line: 'Operating systems and bidding networks that take construction complexity and make extended growth practical.',
  },
  {
    label: 'Money-management tooling',
    line: 'Platforms that grow capital today and absorb a share of profits as the broader network expands.',
  },
  {
    label: 'Talent & software services',
    line: 'Employment and software capacity that serves businesses inside and outside the network on disclosed commercial terms.',
  },
];

export default function LandingPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<'idle' | 'ok' | 'err'>('idle');
  const [accessMode, setAccessMode] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setStatus('idle');
    try {
      await api.post(accessMode ? '/access-requests' : '/contact', {
        name,
        email,
        message,
      });
      setStatus('ok');
      setName('');
      setEmail('');
      setMessage('');
    } catch {
      setStatus('err');
    }
  }

  return (
    <div className="min-h-screen bg-ink-950 text-mist-50">
      <header className="absolute inset-x-0 top-0 z-20 flex items-center justify-between px-6 py-5 md:px-10">
        <BrandLogo variant="onDark" imgClassName="h-9 md:h-10" />
        <nav className="flex items-center gap-5 text-sm text-mist-100/80">
          <a href="#ecosystem" className="hover:text-mist-50 transition">
            Ecosystem
          </a>
          <a href="#contact" className="hover:text-mist-50 transition">
            Contact
          </a>
          <Link
            to="/login"
            className="border border-navy-400/60 px-3 py-1.5 text-navy-400 hover:bg-navy-900/40 transition"
          >
            Investor access
          </Link>
        </nav>
      </header>

      <section className="relative min-h-screen overflow-hidden">
        <div
          className="absolute inset-0 animate-slow-pan bg-cover bg-center"
          style={{
            backgroundImage:
              'linear-gradient(115deg, rgba(7,11,22,0.88) 0%, rgba(16,32,112,0.55) 42%, rgba(7,11,22,0.82) 100%), url(https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=2400&q=80)',
          }}
        />
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_30%_20%,rgba(26,54,120,0.35),transparent_55%)]" />
        <div className="relative z-10 flex min-h-screen flex-col justify-end px-6 pb-20 pt-32 md:px-10 md:pb-28">
          <BrandLogo
            variant="onDark"
            to={null}
            className="animate-fade-in"
            imgClassName="h-14 w-auto md:h-20 lg:h-24"
          />
          <h1 className="animate-fade-up mt-8 max-w-2xl font-display text-2xl font-medium text-mist-100 md:text-3xl [animation-delay:120ms]">
            Construction. Real estate. Technology.
          </h1>
          <p className="animate-fade-up mt-4 max-w-xl text-base leading-relaxed text-mist-200 md:text-lg [animation-delay:220ms]">
            Consular Capital has built an ecosystem at that intersection—and
            sponsors focused funds so partners can put capital to work where the
            network already operates.
          </p>
          <div className="animate-fade-up mt-10 flex flex-wrap gap-4 [animation-delay:320ms]">
            <Link
              to="/login"
              className="bg-navy-700 px-6 py-3 text-sm font-semibold tracking-wide text-mist-50 hover:bg-navy-600 transition"
            >
              Investor access
            </Link>
            <a
              href="#contact"
              className="border border-mist-100/40 px-6 py-3 text-sm tracking-wide text-mist-50 hover:border-mist-50 transition"
            >
              Arrange a conversation
            </a>
          </div>
        </div>
      </section>

      <section className="border-t border-ink-800 bg-ink-900 px-6 py-24 md:px-10">
        <p className="text-xs uppercase tracking-[0.22em] text-navy-400">
          The platform
        </p>
        <h2 className="mt-3 max-w-2xl font-display text-3xl text-mist-50 md:text-4xl">
          One ecosystem. Four working parts.
        </h2>
        <p className="mt-4 max-w-2xl text-sm leading-relaxed text-mist-200 md:text-base">
          Each piece is market-tested and growing. Together they reinforce each
          other—capital funds work, work consumes software, and profits
          strengthen the platform.
        </p>
        <div className="mt-14 grid gap-12 sm:grid-cols-2 lg:grid-cols-4">
          {pillars.map((p) => (
            <div key={p.title}>
              <h3 className="font-display text-xl text-mist-50">{p.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-mist-200">
                {p.body}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section
        id="ecosystem"
        className="border-t border-ink-800 bg-ink-950 px-6 py-24 md:px-10"
      >
        <p className="text-xs uppercase tracking-[0.22em] text-navy-400">
          At work
        </p>
        <h2 className="mt-3 font-display text-3xl text-mist-50 md:text-4xl">
          Where capital meets the network.
        </h2>
        <p className="mt-4 max-w-2xl text-sm leading-relaxed text-mist-200 md:text-base">
          Today the focus is light commercial and residential. The horizon
          sharpens commercial toward energy and utilities, and residential
          toward specialty construction.
        </p>
        <ul className="mt-14 divide-y divide-ink-800 border-y border-ink-800">
          {work.map((w) => (
            <li
              key={w.label}
              className="grid gap-2 py-6 md:grid-cols-[240px_1fr] md:gap-10"
            >
              <span className="font-display text-lg text-mist-50">
                {w.label}
              </span>
              <span className="text-sm leading-relaxed text-mist-200">
                {w.line}
              </span>
            </li>
          ))}
        </ul>
      </section>

      <section className="border-t border-ink-800 bg-ink-900 px-6 py-24 md:px-10">
        <h2 className="font-display text-3xl text-mist-50">
          Capital with a clear destination.
        </h2>
        <p className="mt-4 max-w-2xl text-mist-200 leading-relaxed">
          Limited partners subscribe to a specific fund—chosen for its thesis,
          geography, and timeline. Real estate vehicles are structured toward a
          target ~18% IRR over a typical hold. Consular owns the general
          partner; the fund owns the investments. That capital puts construction
          to work, draws on software and tooling from the network, and returns
          to investors while a share of profits strengthens the platform—on
          commercial terms that are disclosed up front.
        </p>
      </section>

      <section
        id="contact"
        className="relative border-t border-ink-800 bg-mist-50 px-6 py-24 text-ink-900 md:px-10 overflow-hidden"
      >
        <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-navy-900/5" />
        <div className="pointer-events-none absolute -bottom-16 -left-16 h-56 w-56 rounded-full bg-navy-700/5" />
        <div className="relative mx-auto grid max-w-5xl gap-12 md:grid-cols-2">
          <div>
            <BrandLogo imgClassName="h-10 md:h-12" to={null} />
            <h2 className="mt-8 font-display text-3xl">
              A private conversation
            </h2>
            <p className="mt-4 text-ink-700 leading-relaxed">
              Consular works with investors who already know us—or are prepared
              to earn that relationship. Portal access is by invitation, for
              preexisting substantive relationships only.
            </p>
          </div>
          <form onSubmit={onSubmit} className="space-y-4">
            <label className="block text-sm">
              Name
              <input
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1 w-full border-ink-700/30 bg-white"
              />
            </label>
            <label className="block text-sm">
              Email
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full border-ink-700/30 bg-white"
              />
            </label>
            <label className="block text-sm">
              Message
              <textarea
                rows={4}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="mt-1 w-full border-ink-700/30 bg-white"
              />
            </label>
            <label className="flex items-center gap-2 text-sm text-ink-700">
              <input
                type="checkbox"
                checked={accessMode}
                onChange={(e) => setAccessMode(e.target.checked)}
              />
              Request investor portal access
            </label>
            <button
              type="submit"
              className="bg-navy-900 px-5 py-2.5 text-sm font-semibold text-mist-50 hover:bg-navy-700"
            >
              Send
            </button>
            {status === 'ok' && (
              <p className="text-sm text-ink-700">
                Received. We will be in touch.
              </p>
            )}
            {status === 'err' && (
              <p className="text-sm text-red-700">
                Something went wrong. Email john@consularcapital.com.
              </p>
            )}
          </form>
        </div>
      </section>

      <footer className="border-t border-ink-800 bg-ink-950 px-6 py-10 text-xs leading-relaxed text-mist-200/70 md:px-10">
        <BrandLogo variant="onDark" to={null} imgClassName="h-7 opacity-90" />
        <p className="mt-5 max-w-3xl">
          This website is for general information only. Nothing here is an offer
          to sell or a solicitation of an offer to buy any securities. Any
          private placement would be made only pursuant to confidential offering
          documents under Regulation D, Rule 506(b), to accredited investors
          with a preexisting substantive relationship. No general solicitation.
        </p>
      </footer>
    </div>
  );
}
